import { QUESTION_TYPES } from '@api/instant_survey';

/** Sentinel for respondents not covered by a span's explicit points. */
export const ALL_OTHERS_VALUE = '__ALL_OTHERS__';
export const ALL_OTHERS_LABEL = 'All Others';

export const CONFIDENCE_LEVELS = [90, 95, 99];
export const DEFAULT_CONFIDENCE = 95;

export const DEFAULT_BASE_THRESHOLD = 30;

/** Export column keys run from a–ax, limiting banners to 50 columns. */
export const MAX_BANNER_COLUMNS = 50;

/** Sources above the threshold use a value picker, where selections are capped. */
export const VALUE_PICKER_CAP = 10;
export const VALUE_PICKER_THRESHOLD = 10;

export const FILTER_LOGIC = {
  AND: 'AND',
  OR: 'OR',
};

export const SOURCE_CATEGORIES = {
  DEMOGRAPHIC: 'demographic',
  OVERALL: 'overall',
  QUESTION: 'question',
  QUOTA_GROUP: 'quota_group',
  TRIP_ATTRIBUTE: 'trip_attribute',
};

/** Matches the category order in the global-filters dropdown. */
export const SOURCE_CATEGORY_ORDER = [
  { id: SOURCE_CATEGORIES.OVERALL, label: 'Overall' },
  { id: SOURCE_CATEGORIES.QUOTA_GROUP, label: 'Quota Groups' },
  { id: SOURCE_CATEGORIES.DEMOGRAPHIC, label: 'Demographics' },
  { id: SOURCE_CATEGORIES.TRIP_ATTRIBUTE, label: 'Trip Attributes' },
  { id: SOURCE_CATEGORIES.QUESTION, label: 'Questions' },
];

export const OVERALL_SOURCE_ID = 'overall';
export const QUOTA_GROUP_SOURCE_ID = 'quota_group';

export const ROW_ELIGIBLE_QUESTION_TYPES = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.CONT_SUM.value,
  QUESTION_TYPES.CONTSUM.value, // Calypso uses `CONTSUM` for continuous-sum questions.
  // Calypso and Alchemer use different numeric type identifiers.
  QUESTION_TYPES.NUMERIC.externalType,
  QUESTION_TYPES.NUMERIC.value,
  QUESTION_TYPES.RADIO.value,
  QUESTION_TYPES.RANK.value,
  QUESTION_TYPES['RANK-DRAGDROP'].value, // Calypso uses `RANK-DRAGDROP` for rank questions.
  QUESTION_TYPES.TABLE.value,
  QUESTION_TYPES['TABLE-RADIO'].value,
];

/** Option-based questions support row nets; metric-based questions do not. */
export const NET_ELIGIBLE_QUESTION_TYPES = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.RADIO.value,
  QUESTION_TYPES.TABLE.value,
  QUESTION_TYPES['TABLE-RADIO'].value,
];

export const RANK_QUESTION_TYPES = [
  QUESTION_TYPES.RANK.value,
  QUESTION_TYPES['RANK-DRAGDROP'].value,
];

/** Rank tables default to reporting the percentage ranked in the top two. */
export const DEFAULT_RANK_TOP_N = 2;

/** Fixed (non-toggleable) metric per mean-based question type. */
export const QUESTION_TYPE_METRIC_LABELS = {
  [QUESTION_TYPES.CONT_SUM.value]: 'Mean allocation',
  [QUESTION_TYPES.CONTSUM.value]: 'Mean allocation',
  [QUESTION_TYPES.NUMERIC.externalType]: 'Mean',
  [QUESTION_TYPES.NUMERIC.value]: 'Mean',
};

export const CUT_ELIGIBLE_QUESTION_TYPES = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.RADIO.value,
];

/** Raw-data column headers recognized as trip / purchase attributes, in dropdown order. */
export const TRIP_ATTRIBUTE_HEADERS = [
  'Sector',
  'Department',
  'Major Category',
  'Category',
  'Subcategory',
  'Parent Brand',
  'Brand',
  'Parent Channel',
  'Channel',
  'Retailer',
  'Banner',
];

/**
 * Grouped presets for Age and Income, matching the platform's standard rollups.
 * Grouped bins are atomic: they can be combined into larger nets but never split
 * back to granular values. Bins whose member values are absent from the survey's
 * filter options are dropped (e.g. non-US income bands).
 */
export const GROUPED_DEMOGRAPHIC_PRESETS = {
  age_bracket: {
    bins: [
      { label: '18-34', values: ['18-20', '21-24', '25-34'] },
      { label: '35-54', values: ['35-44', '45-54'] },
      { label: '55+', values: ['55-64', '65+'] },
    ],
    label: 'Age (Grouped)',
  },
  income: {
    bins: [
      { label: 'Low Income (Under $40k)', values: ['- $20k', '$20k-40k'] },
      {
        label: 'Middle Income ($40k-$125k)',
        values: ['$40k-60k', '$60k-80k', '$80k-100k', '$100k-125k'],
      },
      { label: 'High Income (Over $125k)', values: ['$125k +'] },
    ],
    label: 'Income (Grouped)',
  },
};

export const QUESTION_TYPE_BADGES = {
  [QUESTION_TYPES.CHECKBOX.value]: 'Multi',
  [QUESTION_TYPES.CONT_SUM.value]: 'Cont. Sum',
  [QUESTION_TYPES.CONTSUM.value]: 'Cont. Sum',
  [QUESTION_TYPES.NUMERIC.externalType]: 'Numeric',
  [QUESTION_TYPES.NUMERIC.value]: 'Numeric',
  [QUESTION_TYPES.RADIO.value]: 'Single',
  [QUESTION_TYPES.RANK.value]: 'Rank',
  [QUESTION_TYPES.TABLE.value]: 'Grid',
  [QUESTION_TYPES['RANK-DRAGDROP'].value]: 'Rank',
  [QUESTION_TYPES['TABLE-RADIO'].value]: 'Grid',
};
